import { EmailTemplate } from '@/models/EmailTemplate';
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import firebase from 'firebase';
import { getUniqueObjectsFromArrayByKey } from '@/utils/getUniqueObjectsFromArrayByKey';
import { FamilyHubUserStatus } from '@/enums/FamilyHubUserStatus';
import { ResidentStatus } from '@/enums/ResidentStatus';
import { FamilyHubAppStatus } from '@/enums/FamilyHubAppStatus';
import AddFamilyMemberDialog from '@/views/family-members/add-family-member-dialog/AddFamilyMemberDialog.vue';
import { getFormattedDateTimeString } from '@/utils/dateTimeFormat';
import Editor from '@/components/editor/Editor.vue';

export default {
  components: {
    Editor,
    ValidationObserver,
    ValidationProvider,
    AddFamilyMemberDialog
  },
  data() {
    return {
      dialogs: {
        broadcast: false,
        testEmail: false,
      },
      loadings: {
        testEmail: false,
        broadcast: false,
      },
      filters: {
        resident: '',
      },
      broadcastForm: {
        template: '',
        html: '',
        email: false,
        push: false,
        sms: false,
      },
      testEmailForm: {
        email: '',
      },
      scrollElement: document.body,
      selectedPageNumber: 1,
      selectedItemsPerPage: 10,
      scrollYPosition: 0,
      selectedScrollYPositionStorageItemName: 'familyMembersSelectedScrollYPosition',
      selectedItemsPerPageStorageItemName: 'familyMembersSelectedItemsPerPage',
      selectedPageNumberStorageItemName: 'familyMembersSelectedPageNumber',
      headers: [
        {
          sortable: false,
          width: 70,
          value: 'imageUrl',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['FamilyList.Name'],
          align: 'start',
          value: 'name',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Resident'],
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Email'],
          value: 'email',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.PhoneNumber'],
          value: 'phoneNumber',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['FamilyList.FamilyAppStatus'],
          value: 'appStatus',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['FamilyList.LastActive'],
          value: 'lastViewedNotifications',
        },
      ],
      pagination: {
        loading: true,
        pageSize: 200,
        isLoadMoreButtonEnabled: true,
        page: 1
      },
      familyMembers: [],
      allFamilyMembers: [],
      loadingFamilyMembers: true,
      showArchivedFamilyMembers: false,
      familyHubAppStatuses: ['All', ...Object.values(FamilyHubAppStatus)],
      familyHubStatusFilter: '',
      search: '',
      addFamilyMemberDialog: false
    };
  },
  computed: {
    ...mapState<{
      broadcastTemplates: EmailTemplate[];
      facility: any;
    }>({
      broadcastTemplates: state => state.facilityModule.broadcastTemplates,
      facility: state => state.facilityModule.selectedFacility,
    }),
    residents() {
      const residents = getUniqueObjectsFromArrayByKey('id', this.familyMembers.reduce((pn, u) => [...pn, ...u.residents || []], []))
      return [
        {
          id: '',
          name: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.All'],
        },
        ...residents.map((r: any) => r.name)
      ];
    },
    filteredFamilyMembers() {
      return this.familyMembers.map(x => { const r = x; r.residentName = (r.residents || []).map(y => y.name).join(','); return x })
        .filter(member => member.residentName.toLowerCase().indexOf(this.filters.resident.toLowerCase()) !== -1)
    },
    isEnableToSend() {
      return (this.broadcastForm.email || this.broadcastForm.push) && (this.broadcastForm.html || this.broadcastForm.template);
    },
    isEnableToSendTestEmail() {
      return this.broadcastForm.html || this.broadcastForm.template;
    },
    getSelectedPageNumber() {
      return parseInt(localStorage.getItem(this.selectedPageNumberStorageItemName)) || this.selectedPageNumber
    },
    getSelectedItemsPerPage() {
      return parseInt(localStorage.getItem(this.selectedItemsPerPageStorageItemName)) || this.selectedItemsPerPage
    },
    getScrollYPosition() {
      return parseInt(localStorage.getItem(this.selectedScrollYPositionStorageItemName)) || 0
    },
  },
  async mounted() {
    
    this.loadingFamilyMembers = true
    await this.$store.dispatch('getFacility');
    await this.loadFamilyMembers();
    await this.getPaginatedFamilyMembers();
    await this.$store.dispatch('getBroadcastTemplates');
    this.loadingFamilyMembers = false

    this.scrollElement.addEventListener("scroll", this.saveScrollPosition);
    await this.setFilters()
  },

  beforeDestroy() {
    this.scrollElement.removeEventListener("scroll", this.saveScrollPosition);
  },

  updated() {
    // Set the scroll position when the component is updated
    this.setScrollYPosition()
  },

  methods: {
    async loadFamilyMembers() {
      const query = firebase
        .firestore()
        .collection('familyHubUsers')
        .where('facilityId', '==', this.facility.id)
        .orderBy('name', 'asc')

      const querySnapshot = await query.get();

      const result = querySnapshot.docs.map(document => {
        const member = {...document.data()};
        const lastActive = member?.lastActive?.seconds? new Date(member?.lastActive.seconds * 1000): null
        member.id = document.id
        member.residents = member?.residents || []
        member.residentsNames = member.residents?.map(resident => resident.name).join(',')
        member.phoneNumber = member.phoneNumber || ''
       
        if(lastActive){
          member.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, this.facility.dateFormat, this.facility.timeFormat, false)
          member.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, this.facility.dateFormat, this.facility.timeFormat, true)
        }
        else{
          member.lastActiveShortDisplay = '-'
          member.lastActiveFullDisplay = '-'
        }
       
        return member
      });

      this.allFamilyMembers = result

      console.log(`loaded ${this.allFamilyMembers.length} family members`)
    },

    async setFilters() {
     await setTimeout(() => {
        this.setPageNumber()
        this.setItemsPerPage()
        this.setScrollYPosition()
      }, 200)
    },

    async familyMembersAdded() {
      return await this.loadFamilyMembers()
    },

    async sendBroadcast() {
      this.loadings.broadcast = true;

      this.$store
        .dispatch('sendBroadcast', {
          ...this.broadcastForm,
          status: 'queued',
        })
        .then(() => {
          this.loadings.broadcast = false;
          this.closeBroadcastDialog()
        });
    },

    closeBroadcastDialog(){
      this.dialogs.broadcast = false;
      this.broadcastForm = {
        template: '',
        html: '',
        email: false,
        push: false,
        sms: false,
      },
      this.testEmailForm = {
        email: ''
      }
    },

    onTemplateChange(templateId) {
      const template = this.broadcastTemplates.find(template => template.id === templateId);
      this.broadcastForm.html = template.html;
    },

    sendTestEmail() {
      this.loadings.testEmail = true;
      this.$refs.testEmailFormObserver.validate();

      let subject = ''
      let html = ''

      if(this.broadcastForm.template && !this.broadcastForm.html) {
        const template = this.broadcastTemplates.find(template => template.id === this.broadcastForm.template);
        subject = template.subject
        html = template.html
      }
      else{
        subject = 'Broadcast Email'
        html = this.broadcastForm.html
      }

      this.$store
        .dispatch('sendTestEmail', {
          message: {
            html,
            subject,
          },
          to: this.testEmailForm.email,
        })
        .then(() => {
          this.broadcastForm = {
            template: '',
            html: '',
            email: false,
            push: false,
            sms: false
          }
          this.loadings.testEmail = false;
          this.dialogs.testEmail = false;
        });
    },

    pageChange(newPage) {
      localStorage.setItem(this.selectedPageNumberStorageItemName, newPage);
    },

    itemsPerPageChange(itemsPerPage) {
      localStorage.setItem(this.selectedItemsPerPageStorageItemName, itemsPerPage);
      this.resetScrollPosition()
    },

    setPageNumber() {
      this.selectedPageNumber = this.getSelectedPageNumber
    },

    setItemsPerPage() {
      this.selectedItemsPerPage = this.getSelectedItemsPerPage
    },

    saveScrollPosition(e) {
      this.scrollYPosition = e.currentTarget.scrollTop;
      localStorage.setItem(this.selectedScrollYPositionStorageItemName, this.scrollYPosition);
    },

    setScrollYPosition() {
      //set vertical scroll position if selected items per page is All or > 15
      const selectedItemsPerPage = this.getSelectedItemsPerPage
      if (selectedItemsPerPage === -1 || selectedItemsPerPage >= 15) {
        this.scrollYPosition = this.getScrollYPosition
        this.scrollElement.scrollTo(0, this.scrollYPosition);
      }
    },

    resetScrollPosition() {
      this.scrollYPosition = 0
      this.scrollElement.scrollTo(0, 0);
      localStorage.setItem(this.selectedScrollYPositionStorageItemName, '0');
    },

    showArchivedFamilyMembersChnaged(e) {
      this.search = ''
      this.filterByArchivedFamilyMembers()
    },

    filterByArchivedFamilyMembers() {
      this.search = ''

      const familyHubAppStatusFilter = this.familyHubStatusFilter.toLowerCase()

      if (this.showArchivedFamilyMembers) {
        if (familyHubAppStatusFilter.length && familyHubAppStatusFilter !== 'all') {
          this.familyMembers = this.allFamilyMembers.filter(x => x.appStatus.toLowerCase() === familyHubAppStatusFilter && (x.status === FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status === ResidentStatus.Departed)))
        }
        else {
          this.familyMembers = this.allFamilyMembers.filter(x => (x.status === FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status === ResidentStatus.Departed)))
        }
      }
      else {
        if (familyHubAppStatusFilter.length && familyHubAppStatusFilter !== 'all') {
          this.familyMembers = this.allFamilyMembers.filter(x => (x.appStatus.toLowerCase() === familyHubAppStatusFilter && (x.residents || []).some(y => y.status !== ResidentStatus.Departed)))
        }
        else {
          this.familyMembers = this.allFamilyMembers.filter(x => (x.status !== FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status !== ResidentStatus.Departed)))
        }
      }

      return this.familyMembers
    },

    filterByFamilyHubStatus(array1, array2) {

      const familyHubAppStatusFilter = this.familyHubStatusFilter.toLowerCase()
      if (familyHubAppStatusFilter.length && familyHubAppStatusFilter !== 'all') {
        if (this.showArchivedFamilyMembers === true) {
          array1 = array2.filter(x => x.appStatus.toLowerCase() === familyHubAppStatusFilter && (x.status === FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status === ResidentStatus.Departed)))
        }
        else {
          array1 = array2.filter(x => x.appStatus.toLowerCase() === familyHubAppStatusFilter)
        }
      }
      else {
        if (this.showArchivedFamilyMembers === true) {
          array1 = array2.filter(x => (x.status === FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status === ResidentStatus.Departed)))
        }
        else {
          array1 = array2
        }
      }

      return array1
    },

    onFilterByFamilyHubStatus(){
      this.familyMembers = this.filterByFamilyHubStatus(this.familyMembers, this.allFamilyMembers)
    },

    async onSearch() {

      const search = (this.search || '').toLowerCase()

      if (search.length > 0) {
        const filtered = this.filterByArchivedFamilyMembers().filter(member => member.name.toLowerCase().indexOf(search) !== -1
          || member.email.toLowerCase().indexOf(search) !== -1
          || member.phoneNumber.toLowerCase().indexOf(search) !== -1
          || member.residentsNames.toLowerCase().indexOf(search) !== -1
        )

        this.familyMembers = filtered;
      }
      else {
        this.familyMembers = this.allFamilyMembers;
        this.filterByArchivedFamilyMembers()
      }
    },

    handleClickFamilyMember(row) {
      this.$router.push('/family-members/' + row.id);
    },

    paginate(array, pageSize, pageNumber) {

      array = this.filterByFamilyHubStatus(array, array)

      return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
    },

    async getPaginatedFamilyMembers() {
      this.showArchivedFamilyMembers = false;
      this.pagination.loading = true;
      try {

        const result = this.paginate(this.allFamilyMembers, this.pagination.pageSize, this.pagination.page)

        this.familyMembers.push(...result);

        this.pagination.isLoadMoreButtonEnabled = result.length >= this.pagination.pageSize;

        this.pagination.page = this.pagination.page + 1

      } catch (e) {
        console.error(e);
      }

      this.pagination.loading = false;
    },

    //TODO: add server side pagination
    async getServerSidePaginated() {
      this.showArchivedFamilyMembers = false;
      this.pagination.loading = true;

      const query = firebase
        .firestore()
        .collection('familyHubUsers')
        .where('facilityId', '==', this.facility.id)
        .orderBy('name', 'asc')
        .startAfter(this.pagination.last)
        .limit(this.pagination.limit);

      try {
        const querySnapshot = await query.get();
        const result = [];

        querySnapshot.docs.forEach(document => {
          if(!this.familyMembers.find(x => x.id === document.id)) {
            const member = document.data();
            const lastActive = member?.lastActive?.seconds? new Date(member?.lastActive.seconds * 1000): null
            member.id = document.id;
            if(lastActive){
              member.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, this.facility.dateFormat, this.facility.timeFormat, false);
              member.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, this.facility.dateFormat, this.facility.timeFormat, true);
            }
            else{
              member.lastActiveShortDisplay = '-';
              member.lastActiveFullDisplay = '-';
            }
            return result.push(member);
          }
        });

        this.familyMembers.push(...result);

        const length = querySnapshot.docs.length;

        this.pagination.last = querySnapshot.docs[length - 1] || null;

        this.pagination.isLoadMoreButtonEnabled = length >= this.pagination.limit;
      } catch (e) {
        console.error(e);
      }

      this.pagination.loading = false;

    },
   
  },
};
